import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { archetypeConfig } from "@/lib/archetypeConfig";

interface Scenario {
  id: string;
  name: string;
  archetype?: string;
  strategic_goal?: string;
  scenario_type: string;
}

interface OptionsReferenceBarProps {
  sessionId: string;
}

export const OptionsReferenceBar = ({ sessionId }: OptionsReferenceBarProps) => {
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const loadScenarios = async () => {
      const { data } = await supabase
        .from("scenarios")
        .select("id, name, archetype, strategic_goal, scenario_type")
        .eq("session_id", sessionId)
        .neq("scenario_type", "baseline")
        .order("order_index");
      
      if (data) setScenarios(data);
    };
    
    loadScenarios();
  }, [sessionId]);

  if (scenarios.length === 0) return null;

  return (
    <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
      <div className="sticky top-16 z-30 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 border border-border rounded-lg shadow-sm -mx-4 md:mx-0">
        {/* Compact header - always visible */}
        <div className="flex items-center justify-between px-4 py-2.5">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-muted-foreground">Optionen:</span>
            
            {/* Compact option pills with tooltips */}
            <div className="flex items-center gap-1.5">
              <TooltipProvider delayDuration={200}>
              {scenarios.map((scenario, index) => {
                  const config = scenario.archetype ? archetypeConfig[scenario.archetype] : null;
                  const letter = String.fromCharCode(65 + index);
                  
                  return (
                    <Tooltip key={scenario.id}>
                      <TooltipTrigger asChild>
                        <div 
                          className={cn(
                            "flex items-center gap-1.5 px-2.5 py-1 rounded-full cursor-help transition-all",
                            "border hover:scale-105",
                            config?.borderColor || "border-border",
                            "bg-background hover:bg-muted/50"
                          )}
                        >
                          {/* Colored dot indicator */}
                          <div className={cn(
                            "w-2 h-2 rounded-full shrink-0",
                            config?.dotColor || "bg-muted-foreground"
                          )} />
                          
                          {/* Letter + short name */}
                          <span className="text-xs font-semibold">{letter}</span>
                          <span className={cn(
                            "text-xs font-medium hidden sm:inline max-w-[80px] truncate",
                            config?.textColor || "text-foreground"
                          )}>
                            {scenario.name.split(' ')[0]}
                          </span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-[280px] p-3">
                        <div className="space-y-1.5">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">{scenario.name}</span>
                            {config && (
                              <Badge variant="outline" className={cn("text-[10px] px-1.5 py-0", config.textColor, config.borderColor)}>
                                {config.label}
                              </Badge>
                            )}
                          </div>
                          {scenario.strategic_goal && (
                            <p className="text-xs text-muted-foreground leading-relaxed">
                              {scenario.strategic_goal}
                            </p>
                          )}
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
              </TooltipProvider>
            </div>
          </div>
          
          {/* Expand/collapse toggle */}
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="h-7 px-2 text-muted-foreground hover:text-foreground">
              <span className="text-xs mr-1">{isExpanded ? "Weniger" : "Details"}</span>
              {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            </Button>
          </CollapsibleTrigger>
        </div>
        
        {/* Expanded content - full option cards */}
        <CollapsibleContent>
          <div className="border-t border-border px-4 py-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {scenarios.map((scenario, index) => {
                const config = scenario.archetype ? archetypeConfig[scenario.archetype] : null;
                const Icon = config?.icon;
                const letter = String.fromCharCode(65 + index);
                
                return (
                  <div 
                    key={scenario.id}
                    className={cn(
                      "p-3 rounded-lg border bg-card transition-colors",
                      config?.borderColor || "border-border"
                    )}
                  >
                    <div className="flex items-start gap-2.5">
                      {/* Letter badge */}
                      <div className={cn(
                        "w-6 h-6 rounded-full flex items-center justify-center shrink-0 text-white text-xs font-bold",
                        config?.dotColor || "bg-muted-foreground"
                      )}>
                        {letter}
                      </div>
                      
                      <div className="min-w-0 flex-1">
                        {/* Name + Archetype */}
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="font-medium text-sm">{scenario.name}</span>
                          {config && Icon && (
                            <Badge 
                              variant="outline" 
                              className={cn("text-[10px] px-1.5 py-0 h-4 gap-0.5", config.textColor, config.borderColor)}
                            >
                              <Icon className="h-2.5 w-2.5" />
                              {config.label}
                            </Badge>
                          )}
                        </div>
                        
                        {/* Strategic goal */}
                        {scenario.strategic_goal && (
                          <p className="text-xs text-muted-foreground leading-relaxed">
                            {scenario.strategic_goal}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};
